/**
 * orders.ts — typed client for the §03 Orders Wave 1 API
 * (server/routes/orders.js).
 *
 * Wave 1 scope: list, detail, create, status transition, assign,
 * collaborator add/remove. Wave 2 (correspondence, files, merge,
 * reopen, etc.) is intentionally absent.
 */
import { apiGet, apiPost, apiPatch, apiDelete, apiPostMultipart } from '@/api/client';
import {
  ORDER_EXPORT_CHUNK_MAX_ROWS,
  runOrdersCsvExport,
} from '@/features/orders/lib/run-orders-csv-export';

export type OrderStatus =
  | 'New'
  | 'In-Process'
  | 'Paused'
  | 'Completed'
  | 'Cancelled'
  | 'Closed';

export interface Order {
  orderId: number;
  requestId: number;
  buildingId: number | null;
  buildingCode: string | null;
  buildingName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  floorNumber: string | null;
  suiteNumber: string | null;
  tenantName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  phoneExt: string | null;
  companyName: string | null;
  companyId: number | null;
  /** Building owner company for the list Company column. Distinct from contact `companyName`. */
  buildingCompanyName?: string | null;
  billToName?: string | null;
  billToEmail?: string | null;
  billToCompanyId?: number | null;
  billToCompanyName?: string | null;
  description: string | null;
  comments: string | null;
  createdAt: string;
  dueDate: string | null;
  startDate: string | null;
  completionDate: string | null;
  closedAt: string | null;
  assignedToUserId: number | null;
  /** Populated when list projection joins assignee name (US-58231 task 08). */
  assignedToName?: string | null;
  /** True when `created_by` ≠ contact email (SSI placed the order). Shown to every role for the avatar. */
  isCreatedBySsi?: boolean;
  /** SSI-only provenance/source label; masked to null for non-SSI callers. */
  source?: string | null;
  /** Non-deleted `request_file` rows linked to this order (list projection). */
  attachmentCount?: number;
  /** Building's effective subscription tier id (SSI-only; null for non-SSI callers). DEF-80. */
  subscriptionTierId?: number | null;
  /** Legacy listing label under Address (SSI-only; e.g. "PRO"); null for non-SSI / no building. */
  subscriptionName?: string | null;
  statusName: string;
  requestType: string | null;
  serviceType: string | null;
  priority: string | null;
  priorityId?: number | null;
  /** FR-146 Low / Med / High. Null until SSI sets it. Distinct from priority. */
  complexity?: 'Low' | 'Med' | 'High' | null;
  pauseReason: string | null;
  ssiReportId?: string | null;
  rowVersion: number;
}

export interface OrderStatusHistoryEntry {
  id: number;
  fromStatus: string | null;
  toStatus: string;
  changedByUserId: number;
  changedByName: string | null;
  changedAt: string;
  reason: string | null;
  notes: string | null;
}

export interface OrderCollaborator {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  roleName: string | null;
  registered: boolean | null;
  isActive: boolean;
  invitedByUserId: number;
  invitedAt: string;
}

export interface OrderListParams {
  view?: 'active' | 'inactive' | 'all';
  search?: string;
  status?: string;
  serviceType?: string;
  companyIds?: string;
  assignedToUserIds?: string;
  priority?: string;
  subscription?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Comma-separated listing column keys for CSV export (`property,address,...`). */
  columns?: string;
  /** `?sort=field` asc, `?sort=-field` desc — server allowlist; export ignores this. */
  sort?: string;
  limit?: number;
  offset?: number;
  /** Chunked CSV export — client-only; stitched by `exportCsv`. */
  exportOffset?: number;
  exportLimit?: number;
  timeZone?: string;
}

export interface OrderListResponse {
  items: Order[];
  total: number;
  limit: number;
  offset: number;
  /** DB-New count in scope + filters; 0 for non-SSI callers. View-agnostic (W1). */
  newCount: number;
}

/** One side of an FR-521 hand-over, as recorded when the member was removed. */
export interface OrderTransferParty {
  userId: number;
  name: string | null;
  email: string | null;
}

/** FR-521: the latest Teams removal that moved this order to its current client contact. */
export interface OrderOwnershipTransfer {
  from: OrderTransferParty;
  to: OrderTransferParty;
  transferredAt: string;
}

export interface OrderDetailResponse {
  order: Order;
  statusHistory: OrderStatusHistoryEntry[];
  collaborators: OrderCollaborator[];
  /** Absent from APIs older than FR-521's hand-over notes. */
  transfer?: OrderOwnershipTransfer | null;
}

export interface OrderDetailLayoutItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  isCollapsed?: boolean;
}

export interface OrderDetailLayoutPreferenceResponse {
  orderDetails: OrderDetailLayoutItem[] | null;
}

export interface CreateOrderPayload {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  phoneExt?: string | null;
  /** Matches `request.company` varchar(50) on the server. */
  companyName?: string | null;
  companyId?: number | null;
  buildingId?: number | null;
  buildingCode?: string | null;
  buildingName?: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  floorNumber?: string | null;
  suiteNumber?: string | null;
  tenantName?: string | null;
  description?: string | null;
  comments?: string;
  serviceTypeId?: number | null;
  serviceType?: string | null;
  priorityId?: number | null;
  /** FR-146. SSI callers only; the server ignores this for non-SSI creates. */
  complexity?: 'Low' | 'Med' | 'High' | null;
  ssiReportId?: string | null;
  assignedToUserId?: number | null;
  idempotencyKey?: string;
  /** Provenance tag (#58219); persisted for SSI callers only. */
  source?: string | null;
  /**
   * SSI Order Date as ISO-8601. Server persists as `request.requestDate` for SSI
   * callers only; omitted/empty stamps now.
   */
  orderDateTime?: string | null;
  billToName?: string | null;
  billToEmail?: string | null;
  billToCompanyId?: number | null;
  billToCompanyName?: string | null;
  collaboratorUserIds?: number[];
}

export interface CreateOrderResponse {
  order: Order;
  idempotent: boolean;
  /** Seed message-log row for this create transaction; pass to file uploads post-create. */
  initialCorrespondenceId: number | null;
}

export interface StatusTransitionPayload {
  toStatus: OrderStatus;
  reason?: string;
  notes?: string;
  rowVersion?: number;
}

export interface AssignPayload {
  assignedToUserId: number | null;
  rowVersion?: number;
}

export interface UpdateOrderPayload {
  rowVersion: number;
  buildingId?: number | null;
  buildingCode?: string | null;
  buildingName?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  floorNumber?: string | null;
  suiteNumber?: string | null;
  tenantName?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  phoneExt?: string | null;
  companyName?: string | null;
  companyId?: number | null;
  description?: string | null;
  comments?: string | null;
  serviceTypeId?: number | null;
  serviceType?: string | null;
  priorityId?: number | null;
  complexity?: 'Low' | 'Med' | 'High' | null;
  ssiReportId?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  assignedToUserId?: number | null;
  source?: string | null;
  billToName?: string | null;
  billToEmail?: string | null;
  billToCompanyId?: number | null;
  billToCompanyName?: string | null;
}

export interface ClientEmailLookupItem {
  userId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  phoneExt: string | null;
  companyId: number | null;
  companyName: string | null;
  registered: boolean | null;
  roleName: string | null;
}

export interface BuildingCodeSearchItem {
  buildingId: number;
  buildingCode: string | null;
  buildingName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** SSI company typeahead row from `GET /orders/lookups/companies/search` (DEF-395). */
export interface CompanySearchLookupItem {
  companyId: number;
  name: string;
}

export interface CompanySearchLookupPage {
  items: CompanySearchLookupItem[];
  page?: number;
  limit?: number;
  total?: number;
  hasMore?: boolean;
}

/** SSI domain resolve from `GET /orders/lookups/company-by-domain` (DEF-395). */
export interface CompanyByDomainLookup {
  companyId: number;
  companyName: string;
}

export interface ServiceTypeOption {
  serviceTypeId: number;
  serviceName: string | null;
  serviceDescription: string | null;
}

export interface PriorityOption {
  priorityId: number;
  priorityName: string;
  priorityDescription: string | null;
}

export interface AssigneeLookupItem {
  userId: number;
  email: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface SubscriptionTierLookupItem {
  subscriptionId: number;
  name: string | null;
}

/** Own-company collaborator search result — mirrors `GET /api/orders/lookups/collaborators` shape. */
export interface CollaboratorLookupItem {
  userId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  roleName: string | null;
  registered: boolean | null;
  isActive: boolean;
}

export interface LookupItemsResponse<T> {
  items: T[];
}

export interface MergeTargetSearchItem {
  orderId: number;
  statusName: string | null;
  buildingCode: string | null;
  buildingName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  orderedByName: string | null;
  companyName: string | null;
  createdAt: string | null;
}

export interface MergeOrderResult {
  sourceOrderId: number;
  targetOrderId: number;
  merged: true;
}

const BASE = '/orders';

export { ORDER_EXPORT_CHUNK_MAX_ROWS };

function withQuery(params: OrderListParams): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (entries.length === 0) return BASE;
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `${BASE}?${qs}`;
}

export const ordersApi = {
  /** List orders for the caller's scope. */
  list(params: OrderListParams = {}): Promise<OrderListResponse> {
    return apiGet<OrderListResponse>(withQuery(params));
  },

  /**
   * Get a single order with status history + collaborators.
   * Suppresses the global 403 permission toast — OrderDetailPage redirects with a
   * neutral info toast instead (DEF-398 / DEF-400).
   */
  detail(orderId: number): Promise<OrderDetailResponse> {
    return apiGet<OrderDetailResponse>(`${BASE}/${orderId}`, undefined, {
      _suppressPermissionToast: true,
    });
  },

  /** Read the authenticated user's order-detail layout preferences. */
  getDetailLayoutPreference(): Promise<OrderDetailLayoutPreferenceResponse> {
    return apiGet<OrderDetailLayoutPreferenceResponse>(`${BASE}/preferences/detail-layout`);
  },

  /** Persist the authenticated user's order-detail layout preferences. */
  saveDetailLayoutPreference(orderDetails: OrderDetailLayoutItem[]): Promise<OrderDetailLayoutPreferenceResponse> {
    return apiPatch<OrderDetailLayoutPreferenceResponse>(`${BASE}/preferences/detail-layout`, { orderDetails });
  },

  /** Create a new order (idempotent via idempotencyKey). */
  create(payload: CreateOrderPayload): Promise<CreateOrderResponse> {
    return apiPost<CreateOrderResponse>(BASE, payload);
  },

  /** SSI-only prefix search for client email autocomplete. */
  searchClientEmails(query: string): Promise<LookupItemsResponse<ClientEmailLookupItem>> {
    return apiGet<LookupItemsResponse<ClientEmailLookupItem>>(
      `${BASE}/lookups/client-emails?q=${encodeURIComponent(query)}`,
    );
  },

  /** SSI-only building code/address search (min 4 characters). */
  searchBuildingCodes(query: string): Promise<LookupItemsResponse<BuildingCodeSearchItem>> {
    return apiGet<LookupItemsResponse<BuildingCodeSearchItem>>(
      `${BASE}/lookups/building-codes/search?q=${encodeURIComponent(query)}`,
    );
  },

  /** SSI-only company typeahead for order create when domain lookup misses (DEF-395). */
  searchCompanies(
    query: string,
    opts: { limit?: number; page?: number } = {},
  ): Promise<CompanySearchLookupPage> {
    const params = new URLSearchParams({ q: query });
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.page != null) params.set('page', String(opts.page));
    return apiGet<CompanySearchLookupPage>(`${BASE}/lookups/companies/search?${params.toString()}`);
  },

  /** SSI-only resolve company by client email domain (DEF-395). */
  lookupCompanyByDomain(domain: string): Promise<CompanyByDomainLookup | null> {
    return apiGet<CompanyByDomainLookup | null>(
      `${BASE}/lookups/company-by-domain?domain=${encodeURIComponent(domain)}`,
    );
  },

  /** Service-type dropdown catalog (scoped for non-SSI callers). */
  async listServiceTypes(): Promise<ServiceTypeOption[]> {
    const response = await apiGet<
      { items: ServiceTypeOption[] }
      | ServiceTypeOption[]
      | { result?: ServiceTypeOption[] }
    >(`${BASE}/lookups/service-types`);
    if (Array.isArray(response)) {
      return response;
    }
    if ('items' in response && Array.isArray(response.items)) {
      return response.items;
    }
    if ('result' in response && Array.isArray(response.result)) {
      return response.result;
    }
    return [];
  },

  /** SSI-only priority dropdown. */
  listPriorities(): Promise<LookupItemsResponse<PriorityOption>> {
    return apiGet<LookupItemsResponse<PriorityOption>>(`${BASE}/lookups/priorities`);
  },

  /** SSI staff assignee directory; optional prefix search on name/email. */
  searchAssignees(query?: string): Promise<LookupItemsResponse<AssigneeLookupItem>> {
    const qs = query ? `?q=${encodeURIComponent(query)}` : '';
    return apiGet<LookupItemsResponse<AssigneeLookupItem>>(`${BASE}/lookups/assignees${qs}`);
  },

  /** SSI-only subscription tier catalog for list filter multi-select. */
  listSubscriptionTiers(): Promise<LookupItemsResponse<SubscriptionTierLookupItem>> {
    return apiGet<LookupItemsResponse<SubscriptionTierLookupItem>>(`${BASE}/lookups/subscriptions`);
  },

  /**
   * Company-scoped collaborator prefix search for non-SSI order create (#58220 Task 07).
   * Calls GET /api/orders/lookups/collaborators?q= — authenticated, NOT SSI-gated.
   * The server enforces own-company scope; non-SSI callers can never see other companies.
   */
  searchCollaborators(query: string): Promise<LookupItemsResponse<CollaboratorLookupItem>> {
    return apiGet<LookupItemsResponse<CollaboratorLookupItem>>(
      `${BASE}/lookups/collaborators?q=${encodeURIComponent(query)}`,
    );
  },

  /** SSI-only merge-target lookup by id/address/orderer/company/building code. */
  searchMergeTargets(query: string, excludeOrderId?: number): Promise<LookupItemsResponse<MergeTargetSearchItem>> {
    const params = new URLSearchParams({ q: query });
    if (excludeOrderId != null) params.set('excludeOrderId', String(excludeOrderId));
    return apiGet<LookupItemsResponse<MergeTargetSearchItem>>(`${BASE}/lookups/merge-targets?${params.toString()}`);
  },

  /** Transition an order's status (server-side state-machine guarded). */
  transitionStatus(orderId: number, payload: StatusTransitionPayload): Promise<{ order: Order }> {
    return apiPost<{ order: Order }>(`${BASE}/${orderId}/status`, payload);
  },

  /** Assign or reassign an order (SSI roles only). */
  assign(orderId: number, payload: AssignPayload): Promise<{ order: Order }> {
    return apiPost<{ order: Order }>(`${BASE}/${orderId}/assign`, payload);
  },

  /** SSI save-all update for detail widget inline edits. */
  update(orderId: number, payload: UpdateOrderPayload): Promise<{ order: Order }> {
    return apiPatch<{ order: Order }>(`${BASE}/${orderId}`, payload);
  },

  /** SSI-only hard delete. */
  deleteOrder(orderId: number): Promise<{ orderId: number; deleted: true }> {
    return apiDelete<{ orderId: number; deleted: true }>(`${BASE}/${orderId}`);
  },

  /** SSI-only hard merge (source order is deleted; artifacts re-parent to target). */
  mergeOrder(orderId: number, targetOrderId: number): Promise<MergeOrderResult> {
    return apiPost<MergeOrderResult>(`${BASE}/${orderId}/merge`, { targetOrderId });
  },

  /** Add or invite a collaborator on an order. */
  addCollaborator(
    orderId: number,
    input: {
      userId?: number;
      inviteEmail?: string;
      inviteCompanyName?: string;
      inviteCompanyId?: number;
      skipMessageLog?: boolean;
    },
  ): Promise<{ orderId: number; userId: number }> {
    return apiPost<{ orderId: number; userId: number }>(`${BASE}/${orderId}/collaborators`, input);
  },

  /** Revoke a collaborator. */
  removeCollaborator(orderId: number, userId: number): Promise<{ orderId: number; userId: number }> {
    return apiDelete<{ orderId: number; userId: number }>(`${BASE}/${orderId}/collaborators/${userId}`);
  },

  /**
   * Generate the SAB record linked to this order (PRD §03 Wave 2 ORD-W2-2).
   * SSI-only on the server; UI should hide the affordance for non-SSI.
   */
  generateSab(orderId: number, payload: GenerateSabPayload): Promise<GenerateSabResponse> {
    return apiPost<GenerateSabResponse>(`${BASE}/${orderId}/sab/generate`, payload);
  },

  // ─── Wave 3 (PRD §03 §3) Correspondence threads ─────────────────

  listCorrespondence(orderId: number, params: { limit?: number; offset?: number } = {}): Promise<CorrespondenceListResponse> {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join('&');
    return apiGet<CorrespondenceListResponse>(`${BASE}/${orderId}/correspondence${qs ? `?${qs}` : ''}`);
  },

  postCorrespondence(orderId: number, payload: PostCorrespondencePayload): Promise<{ correspondence: CorrespondenceEntry }> {
    return apiPost<{ correspondence: CorrespondenceEntry }>(`${BASE}/${orderId}/correspondence`, payload);
  },

  editCorrespondence(orderId: number, msgId: number, payload: EditCorrespondencePayload): Promise<{ correspondenceId: number; editedAt: string; rowVersion: number }> {
    return apiPatch<{ correspondenceId: number; editedAt: string; rowVersion: number }>(
      `${BASE}/${orderId}/correspondence/${msgId}`,
      payload,
    );
  },

  deleteCorrespondence(orderId: number, msgId: number): Promise<{ correspondenceId: number }> {
    return apiDelete<{ correspondenceId: number }>(`${BASE}/${orderId}/correspondence/${msgId}`);
  },

  // ─── Wave 3 (PRD §03 §4) Order files (legacy request_file) ─────

  listOrderFiles(orderId: number): Promise<OrderFileListResponse> {
    return apiGet<OrderFileListResponse>(`${BASE}/${orderId}/files`);
  },

  uploadOrderFile(
    orderId: number,
    file: File,
    client: boolean,
    options?: { requestLogId?: number; isPublic?: boolean },
  ): Promise<{ file: OrderFileEntry }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('client', String(client));
    if (options?.requestLogId != null) {
      fd.append('requestLogId', String(options.requestLogId));
    }
    if (options?.isPublic != null) {
      fd.append('isPublic', String(options.isPublic));
    }
    return apiPostMultipart<{ file: OrderFileEntry }>(`${BASE}/${orderId}/files`, fd);
  },

  getOrderFileDownloadUrl(orderId: number, fileId: number): Promise<{ url: string; fileName: string }> {
    return apiGet<{ url: string; fileName: string }>(`${BASE}/${orderId}/files/${fileId}/download`);
  },

  deleteOrderFile(orderId: number, fileId: number): Promise<{ fileId: number }> {
    return apiDelete<{ fileId: number }>(`${BASE}/${orderId}/files/${fileId}`);
  },

  // ─── Wave 4 — Bulk CSV download (AC-OL-009) ────────────────────

  /** Max rows per export HTTP response — must match server `ORDER_EXPORT_CHUNK_MAX_ROWS`. */
  exportChunkMaxRows: ORDER_EXPORT_CHUNK_MAX_ROWS,

  /**
   * Download the current filtered Orders set as a CSV. Prefer
   * `useOrdersExportStore().startExport` for list-page UX (global progress banner).
   */
  exportCsv(params: OrderListParams = {}): Promise<void> {
    return runOrdersCsvExport(params);
  },

  /** Diagnostic: trigger one auto-close tick (SSI Admin only). */
  runAutoCloseTick(
    options: { thresholdDays?: number; dryRun?: boolean } = {},
  ): Promise<{
    found: number;
    closed: number;
    skipped: number;
    notified: number;
    silent: number;
    dryRun: boolean;
    errors: Array<{ orderId: number | null; message: string }>;
  }> {
    return apiPost(
      `${BASE}/admin/auto-close/run`,
      options,
    );
  },
};

export interface GenerateSabPayload {
  factorMethodId: number;
  acceptedTerms: boolean;
  initials: string;
  marketLoad?: string;
  idempotencyKey?: string;
}

export interface GenerateSabResponse {
  orderId: number;
  reportUpdateId: number;
  reportId: number;
  buildingId: number;
}

// ─── Wave 3 — Correspondence + Attachment types ─────────────────

export interface CorrespondenceEntry {
  correspondenceId: number;
  orderId: number;
  body: string;
  comment: string;
  postedByUserId: number;
  postedByName: string | null;
  postedByCompanyName?: string | null;
  avatar: 'ssi' | 'non-ssi';
  postedAt: string | null;
  logDate: string | null;
  isInternal: boolean;
  isPublic: boolean;
  isClient: boolean;
  orderStatus: string | null;
  sendEmailToClient: boolean;
  recipientEmail: string | null;
  editedAt: string | null;
  rowVersion: number;
  mentions: number[];
  attachments: CorrespondenceAttachment[];
}

export interface CorrespondenceAttachment {
  fileId: number;
  fileName: string;
  fileExtension: string | null;
  fileSize: number | null;
}

export interface CorrespondenceListResponse {
  items: CorrespondenceEntry[];
  total: number;
}

export interface PostCorrespondencePayload {
  body: string;
  isInternal?: boolean;
  isPublic?: boolean;
  status?: string;
  toStatus?: string;
  reason?: string;
  notes?: string;
  sendEmailToClient?: boolean;
  recipientEmail?: string | null;
}

export interface EditCorrespondencePayload {
  body: string;
  rowVersion?: number;
}

export interface OrderFileEntry {
  fileId: number;
  orderId: number;
  requestLogId?: number | null;
  fileName: string;
  fileSize: number | null;
  client: boolean;
  uploadedByUserId: number | null;
  uploadedByName: string | null;
  uploadedAt: string;
  fileExtension: string | null;
}

export interface OrderFileListResponse {
  items: OrderFileEntry[];
}
